import { ChatBubbleOutline, DescriptionOutlined } from '@mui/icons-material'
import { Link, Stack, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { renderConfigAuthor } from '@/features/controller/configuration/components/ConfigAuthorInfo'
import type { LatestConfigurationVersions } from '@/features/controller/configuration/models/controllerConfiguration'
import type { ControllerConfigType, ControllerConfigVersion } from '@/features/controller/configuration/types'
import {
  AppJsonConfigType,
  RaspiPropertiesConfigType,
} from '@/features/controller/configuration/utils/controllerConfigTypes'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { renderRelativePastTimestamp, renderTimestamp } from '@/features/controller/utils/renderTimestamp'

type LatestConfigVersionsInfoProps = {
  controllerId: string
  latestConfigurationVersions: LatestConfigurationVersions
}

/**
 * Background awareness of who last changed each file the configuration tree is built from, and why, so that an edit in
 * the tree doesn't unknowingly undo a deliberate change. The exact time and the comment are in a tooltip.
 */
export function LatestConfigVersionsInfo({
  controllerId,
  latestConfigurationVersions,
}: Readonly<LatestConfigVersionsInfoProps>) {
  const { t, i18n } = useTranslation()

  const files: { configType: ControllerConfigType; configVersion: ControllerConfigVersion }[] = []
  if (latestConfigurationVersions.appJson) {
    files.push({ configType: AppJsonConfigType, configVersion: latestConfigurationVersions.appJson })
  }
  if (latestConfigurationVersions.raspiProperties) {
    files.push({ configType: RaspiPropertiesConfigType, configVersion: latestConfigurationVersions.raspiProperties })
  }

  if (files.length === 0) {
    return null
  }

  return (
    <Stack columnGap={3} direction="row" flexWrap="wrap" sx={{ px: 1, mb: 2 }}>
      {files.map(({ configType, configVersion }) => (
        <Tooltip
          key={configType.id}
          title={
            <>
              <div>{renderTimestamp(configVersion.createdAt, i18n.language)}</div>
              {configVersion.comment && <div>{configVersion.comment}</div>}
            </>
          }
        >
          <Stack alignItems="center" color="text.secondary" direction="row" gap={0.5}>
            <DescriptionOutlined fontSize="small" />
            <CustomTypography color="text.secondary" variant="caption">
              <Link
                color="inherit"
                component={RouterLink}
                to={ControllerRouteInformation.CONTROLLER_CONFIG_VIEW.navigationPath(controllerId, configVersion.id)}
              >
                {configType.name}
              </Link>{' '}
              {t('controllers.configuration.version.changed_at_by', {
                time: renderRelativePastTimestamp(configVersion.createdAt, i18n.language),
                author: renderConfigAuthor(configVersion, t),
              })}
            </CustomTypography>
            {configVersion.comment && <ChatBubbleOutline sx={{ fontSize: 14 }} />}
          </Stack>
        </Tooltip>
      ))}
    </Stack>
  )
}
